import { useParams, useNavigate, Link } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Skeleton, Card, CardContent, Button } from "../components/ui"
import { Shell } from "../components/shell"
import { LIMITS, getPlan } from "../lib/plans"
import { ClipboardList, UserCheck, UserMinus, RefreshCw, SendHorizontal, Lock } from "lucide-react"

const ICONS: Record<string, React.ReactNode> = {
  member_added: <UserCheck className="h-4 w-4 text-green-500" />,
  member_removed: <UserMinus className="h-4 w-4 text-red-500" />,
  role_changed: <RefreshCw className="h-4 w-4 text-blue-500" />,
  invite_sent: <SendHorizontal className="h-4 w-4 text-primary" />,
}

const LABELS: Record<string, string> = {
  member_added: "Member added",
  member_removed: "Member removed",
  role_changed: "Role changed",
  invite_sent: "Invite sent",
}

/** Audit log page. Shows workspace-level events like member additions, role changes, and invites. Gated behind a Pro or Business plan. */
export default function AuditPage() {
  const { workspaceId } = useParams({ from: "/workspaces/$workspaceId/audit" })
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()

  const { data: workspace, isLoading: wsLoading } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ id: string; name: string; role: string; plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })

  const canAudit = LIMITS[getPlan(workspace?.plan)].auditLog
  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin"

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ["audit", workspaceId],
    queryFn: () => fetcher<any[]>(`/api/audit?workspaceId=${workspaceId}`),
    enabled: !!session && !!canAudit && isAdmin,
  })

  if (!session) { navigate({ to: "/login" }); return null }

  const isLoading = wsLoading || logsLoading

  return (
    <Shell
      title={workspace?.name || "Audit log"}
      back={{ to: "/workspaces/$workspaceId", label: "Workspace" }}
      tabs={
        <div className="flex gap-4 text-sm">
          <Link to="/workspaces/$workspaceId" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Projects</Link>
          <Link to="/workspaces/$workspaceId/members" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Members</Link>
          <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="border-b-2 border-primary pb-2 font-medium">Audit log</span>
          {isAdmin && (
            <Link to="/workspaces/$workspaceId/webhooks" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Webhooks</Link>
          )}
        </div>
      }
    >
      <main className="mx-auto w-full max-w-4xl p-6">
        <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold tracking-tight">
          <ClipboardList className="h-5 w-5" /> Audit log
        </h2>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
          </div>
        ) : !canAudit ? (
          <div className="rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5 p-6 text-center">
            <Lock className="mx-auto mb-3 h-8 w-8 text-primary" />
            <h3 className="mb-1 font-semibold">Audit log requires a Pro or Business plan</h3>
            <p className="mb-4 text-sm text-muted-foreground">Track member changes, role updates, and invitations in your workspace.</p>
            <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
              <Button size="sm">Upgrade now</Button>
            </Link>
          </div>
        ) : !isAdmin ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            Only workspace owners and admins can view the audit log.
          </div>
        ) : !logs || logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <ClipboardList className="h-7 w-7 text-muted-foreground" />
            </div>
            <h3 className="mb-1 text-lg font-semibold">No audit events yet</h3>
            <p className="text-sm text-muted-foreground">Member changes and role updates will appear here.</p>
          </div>
        ) : (
          <Card className="overflow-hidden rounded-xl border-border bg-card shadow-sm">
            <CardContent className="p-0">
              {logs.map((log, idx) => {
                const meta = log.meta ? JSON.parse(log.meta) : {}
                let detail = log.targetEmail || log.targetId || ""
                if (log.type === "role_changed") detail = `${meta.from} → ${meta.to}${log.targetId ? ` (${log.targetId})` : ""}`
                if (log.type === "invite_sent") detail = `${log.targetEmail} as ${meta.role}`
                if (log.type === "member_added") detail = `${log.targetEmail || log.targetId} as ${meta.role}`
                return (
                  <div key={log.id} className={`flex items-center gap-3 px-4 py-3 ${idx !== logs.length - 1 ? "border-b border-border" : ""}`}>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                      {ICONS[log.type] || <ClipboardList className="h-4 w-4 text-muted-foreground" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{LABELS[log.type] || log.type}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {log.actor?.name || log.actor?.email || "Unknown"} · {detail}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {new Date(log.createdAt * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        )}
      </main>
    </Shell>
  )
}
